import { useState, useEffect, useMemo, useCallback } from "react";
import { useTenant } from "../../../hooks/useTenant";
import MemberService from "../../../services/member_service";
import { useDebounce } from "../../../hooks/useDebounce";

export const useKiosk = () => {
    const { idTenant, tenantLogo, tenantName, loading: tenantLoading } = useTenant();

    const [members, setMembers] = useState([]);
    const [loadingMembers, setLoadingMembers] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [isKeyboardVisible, setIsKeyboardVisible] = useState(true);
    const [selectedMember, setSelectedMember] = useState(null);
    const debouncedSearchTerm = useDebounce(searchTerm, 600);

    // Carregar todos os membros no carregamento inicial do quiosque
    useEffect(() => {
        if (!tenantLoading && idTenant) {
            setLoadingMembers(true);
            MemberService.getActiveMembers(idTenant)
                .then(data => setMembers(data))
                .catch(err => console.error("Erro no quiosque (members):", err))
                .finally(() => setLoadingMembers(false));
        }
    }, [idTenant, tenantLoading]);

    // Filtra membros via debounce state
    const filteredMembers = useMemo(() => {
        if (!debouncedSearchTerm || debouncedSearchTerm.trim().length < 2) return [];
        const term = debouncedSearchTerm.toLowerCase();

        return members.filter(m =>
            (m.fullName && m.fullName.toLowerCase().includes(term)) ||
            (m.memberNumber && m.memberNumber.includes(term))
        ).slice(0, 50); // Exibe até 50 para evitar sobrecarga de tela touch
    }, [debouncedSearchTerm, members]);

    const handleKeyPress = useCallback((key) => {
        setSearchTerm(prev => prev + key);
    }, []);

    const handleDelete = useCallback(() => {
        setSearchTerm(prev => prev.slice(0, -1));
    }, []);

    const handleSpace = useCallback((() => {
        setSearchTerm(prev => prev + " ");
    }), []);

    const handleClear = useCallback(() => {
        setSearchTerm("");
    }, []);

    const handleSelectMember = useCallback((member) => {
        setSelectedMember(member);
        setIsKeyboardVisible(false);
    }, []);

    const handleBack = useCallback(() => {
        setSelectedMember(null);
    }, []);

    const hideKeyboard = useCallback(() => {
        setIsKeyboardVisible(false);
    }, []);

    const showKeyboard = useCallback(() => {
        setIsKeyboardVisible(true);
    }, []);

    const toggleKeyboard = useCallback(() => {
        setIsKeyboardVisible(prev => !prev);
    }, []);

    return {
        tenantLogo,
        tenantName,
        tenantLoading,
        idTenant,
        members,
        loadingMembers,
        searchTerm,
        isKeyboardVisible,
        selectedMember,
        filteredMembers,
        debouncedSearchTerm,
        actions: {
            handleKeyPress,
            handleDelete,
            handleSpace,
            handleClear,
            handleSelectMember,
            handleBack,
            hideKeyboard,
            showKeyboard,
            toggleKeyboard
        }
    };
};
